import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { selectUser } from '../../features/currentUser/currentUserSelectors';
import { logout } from '../../features/currentUser/currentUserSlice';
import { cognitoSignOutSafe } from '../../apis/authCognito';
import { useHistory } from 'react-router-dom';
import { isTokenExpired } from '../../utils/tokenUtils';

const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes
const COUNTDOWN_DURATION = 60; // 60 seconds
const TOTAL_TIMEOUT = INACTIVITY_TIMEOUT + COUNTDOWN_DURATION * 1000; // 16 minutes total
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

type ModalMode = 'inactive' | 'expired';

export const InactivityMonitor: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { accessToken, refreshToken: storedRefreshToken } = useAppSelector(selectUser);
  const isAuthenticated = !!(accessToken && storedRefreshToken);

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>('inactive');
  const [countdown, setCountdown] = useState(COUNTDOWN_DURATION);

  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const handleLogout = useCallback(async () => {
    setShowModal(false);
    await cognitoSignOutSafe();
    dispatch(logout());
    history.push('/');
  }, [dispatch, history]);

  const checkInactivityAndShowModal = useCallback(() => {
    const elapsed = Date.now() - lastActivityRef.current;

    if (elapsed >= TOTAL_TIMEOUT) {
      // More than 16 min passed - show expired modal (session timed out)
      setModalMode('expired');
      setCountdown(60);
      setShowModal(true);
      return;
    }

    if (elapsed >= INACTIVITY_TIMEOUT) {
      // Between 15-16 min - show modal with remaining countdown
      const remainingMs = TOTAL_TIMEOUT - elapsed;
      const remainingSeconds = Math.ceil(remainingMs / 1000);

      // Check if access token is expired - if so, "Stay Logged In" won't help
      const accessExpired = isTokenExpired(accessToken);
      setModalMode(accessExpired === true ? 'expired' : 'inactive');

      setCountdown(remainingSeconds);
      setShowModal(true);
    }
  }, [accessToken]);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    if (!isAuthenticated) return;

    // Record last activity time
    lastActivityRef.current = Date.now();

    inactivityTimerRef.current = setTimeout(() => {
      checkInactivityAndShowModal();
    }, INACTIVITY_TIMEOUT);
  }, [isAuthenticated, checkInactivityAndShowModal]);

  const handleStayLoggedIn = useCallback(() => {
    setShowModal(false);
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  // Countdown effect - runs when modal is shown (only for inactive mode, not expired)
  useEffect(() => {
    if (!showModal || modalMode === 'expired') return;

    const countdownTimer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownTimer);
          // Switch to expired modal instead of auto-logout
          setModalMode('expired');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownTimer);
  }, [showModal, modalMode]);

  // Check inactivity on visibility change (tab becomes active)
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !showModal) {
        // Tab became visible - check if we should show modal or logout
        checkInactivityAndShowModal();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated, showModal, checkInactivityAndShowModal]);

  // Set up activity listeners
  useEffect(() => {
    if (!isAuthenticated) {
      setShowModal(false);
      return;
    }

    const handleActivity = () => {
      if (!showModal) {
        resetInactivityTimer();
      }
    };

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    resetInactivityTimer();

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [isAuthenticated, showModal, resetInactivityTimer]);

  if (!isAuthenticated) return null;

  const isExpired = modalMode === 'expired';

  return (
    <Modal show={showModal} centered backdrop="static" keyboard={false}>
      <Modal.Header>
        <Modal.Title>
          <h6 className="mb-0">
            {isExpired ? t('inactivity-expired-title') : t('inactivity-title')}
          </h6>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="text-center py-3">
          <p>{isExpired ? t('inactivity-expired-message') : t('inactivity-message')}</p>
          {!isExpired && (
            <>
              <h2 className="my-3">{countdown}</h2>
              <p className="text-muted">{t('inactivity-seconds')}</p>
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className="p-0">
        <div className="w-100 d-flex m-0">
          {isExpired ? (
            <Button
              variant="outline-dark"
              className="w-100 p-3 border-0 rounded-0"
              onClick={handleLogout}
            >
              {t('inactivity-ok')}
            </Button>
          ) : (
            <>
              <Button
                variant="outline-dark"
                className="w-100 p-3 border-0 border-end rounded-0"
                onClick={handleLogout}
              >
                {t('inactivity-logout')}
              </Button>
              <Button
                variant="outline-dark"
                className="w-100 p-3 border-0 rounded-0"
                onClick={handleStayLoggedIn}
              >
                {t('inactivity-stay')}
              </Button>
            </>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};
